<script setup lang="ts">
/**
 * *Clean* — wearing no title, offered as the first card on the Titles shelf.
 *
 * ── Why this is a card and not the strip it used to be ───────────────────────
 *
 * It was a full-width banner above the grid, drawn only while a title was
 * actually worn, saying where a title appears and carrying one button reading
 * *Wear none*. Two things were wrong with that, and they are the same thing
 * twice: **not wearing a title is a choice, and the shelf was not offering it.**
 *
 * A banner reads as a statement about the tab, so the one control on it read as
 * an undo rather than as an option — and because it was drawn only when a title
 * was on, a player wearing nothing saw a shelf of ten titles with no indication
 * that the bare name they currently had was one of the eleven available looks.
 * The state they were in was the one state the screen never showed. Every other
 * slot in the garage draws its current fitting as a card with FITTED on it; this
 * one hid it.
 *
 * So it is a card now, always present, first in the grid, and it wears the same
 * `Worn` badge and the same accent border an ItemCard wears when it is the one
 * in use. Nothing is bought and nothing is spent — the copy says so where a
 * price would go.
 *
 * ── Why it is not an `ItemCard` ──────────────────────────────────────────────
 *
 * Because there is no item. Making one would mean inventing a catalogue row with
 * a tier, a price, an upgrade ceiling and a research gate — twenty-odd fields of
 * fiction, every one of which `ItemCard` would then faithfully draw — to
 * describe the *absence* of a purchase. The three things this card genuinely
 * shares with its neighbours are the chrome, the plate preview and the footer
 * button, and those are restated below rather than reached for.
 *
 * That is a real duplication and it is worth naming: if `ItemCard`'s card
 * chrome moves, this has to move with it. The alternative was `ItemCard`
 * growing a second mode for a thing that is not an item, and a shared component
 * used by four shelves is the worse place to put a special case than a
 * forty-line neighbour is.
 */
defineProps<{
  /** True while a title is worn — so this card is the *other* option. */
  worn: boolean;
  /** Any garage mutation in flight. Every control on the floor disables together. */
  busy: boolean;
  /** The player's own call sign, shown on the mock plate. */
  nickname: string;
}>();

const emit = defineEmits<{ (e: 'remove'): void }>();
</script>

<template>
  <article class="card" :class="{ equipped: !worn }" data-item="title-none">
    <header>
      <div class="title">
        <h3>Clean</h3>
        <!-- No tier line. A tier is a rung on a ladder somebody climbed; this is
             the ground. -->
      </div>
      <span v-if="!worn" class="badge fitted">Worn</span>
      <span v-else class="badge free">No cost</span>
    </header>

    <div class="title-preview">
      <!-- Deliberately no badge above the plate. The gap *is* the product: what
           the shelf's other cards show at full size, this one has none of. -->
      <div class="title-plate" aria-label="As it appears over a tank">
        <span class="title-plate-name">{{ nickname || 'Your name' }}</span>
        <span class="title-plate-bar"><i></i></span>
      </div>
    </div>

    <p class="blurb">
      Your call sign on its own, over your tank, in the scoreboard and in the
      kill feed. Nothing above it.
    </p>

    <footer>
      <button
        type="button"
        class="primary"
        data-action="remove-title"
        :disabled="!worn || busy"
        @click="emit('remove')"
      >
        {{ worn ? 'Wear none' : 'Worn' }}
      </button>
    </footer>
  </article>
</template>

<style scoped>
/* The chrome, restated from `ItemCard` — see the header note for why it is a
   copy. Anything that changes here changes there. */
.card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-800);
}

.card.equipped {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px rgba(232, 114, 44, 0.25);
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

header .title {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

h3 {
  margin: 0;
  font-size: 1rem;
  letter-spacing: 0.05em;
  color: #fff;
}

.badge {
  flex: none;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--steel-200);
}

.badge.fitted {
  border-color: var(--accent);
  color: var(--accent);
}

/* Where a price goes on every other card. Drawn the quiet way `.price.unsold`
   is, because it is a label and not a figure — the same argument, and it has to
   reach the same conclusion or the shelf reads as having one free item on it. */
.badge.free {
  color: var(--steel-400);
}

/* The plate strip, at the arena's own size. Same ground as `ItemCard`'s, so a
   title card and this one show the same battlefield behind the same name. */
.title-preview {
  display: grid;
  gap: 0.6rem;
  justify-items: center;
  padding: 1rem 0.8rem 0.8rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(255, 255, 255, 0.05), transparent 70%),
    #14181e;
}

/* No `border-top` here, unlike `ItemCard`'s: that rule separates the plate from
   the big badge above it, and there is nothing above this one to separate it
   from. */
.title-plate {
  display: grid;
  justify-items: center;
  gap: 2px;
  padding: 0.35rem 0.6rem 0.45rem;
  width: 100%;
}

.title-plate-name {
  font-size: 0.62rem;
  color: var(--steel-200);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

.title-plate-bar {
  display: block;
  width: 56px;
  height: 5px;
  border: 1px solid rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.title-plate-bar i {
  display: block;
  width: 72%;
  height: 100%;
  background: #4caf6d;
}

.blurb {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--steel-400);
}

footer {
  margin-top: auto;
  display: flex;
  gap: 0.4rem;
}

footer button {
  flex: 1;
  padding: 0.55rem 0.5rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-700);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  cursor: pointer;
}

footer button.primary {
  border-color: transparent;
  background: var(--accent);
  color: #12161c;
  font-weight: 600;
}

footer button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* The tap floor the rest of this shelf is held to on a phone held sideways.
   `.garage.compact` is `GarageScreen`'s one `matchMedia` answer and is an
   ancestor here, exactly as it is for `ItemCard`'s identical rule. */
.garage.compact .card footer button {
  min-height: 44px;
}
</style>
